import { Form, Select } from 'antd';
import { Lightbulb } from 'lucide-react';

import { Divider, Space } from '@/components/base';
import Modal from '@/components/Modal/Modal';
import { ModalLayout } from '@/components/Modal/styles';
import Typo from '@/components/Typography/Typo';
import useModalState from '@/hooks/pure/useModalState';
import zIndex from '@/styles/zIndex';

import { SCRAPER_REQ_FIELD_KEY } from '../../configs/scraper-field-map/field-const';

export const KeywordsField = (props: { required?: boolean }) => {
  const { required = false } = props;
  const modal = useModalState();
  return (
    <>
      <Form.Item
        name={SCRAPER_REQ_FIELD_KEY.KEYWORDS}
        hidden
        required={required}
        rules={
          required
            ? [
                {
                  type: 'array',
                  required: true,
                  message: 'At least one keyword is required',
                  min: 1,
                },
              ]
            : []
        }
      >
        <input />
      </Form.Item>
      <Form.Item
        label="Keywords"
        required={required}
        rules={[
          // 이 Form.Item은 에러 표시용
          ({ getFieldValue }) => ({
            validator() {
              const keywords = getFieldValue(SCRAPER_REQ_FIELD_KEY.KEYWORDS);
              if (required && (!keywords || keywords.length === 0)) {
                return Promise.reject(
                  new Error('At least one keyword is required'),
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
        shouldUpdate
      >
        {({ getFieldValue, setFieldValue, getFieldError }) => {
          const currentKeywords: string[] =
            getFieldValue(SCRAPER_REQ_FIELD_KEY.KEYWORDS) || [];
          const errors = getFieldError(SCRAPER_REQ_FIELD_KEY.KEYWORDS);
          return (
            <div className="flex-1 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Select
                  placeholder="Type keywords"
                  style={{ flex: 1 }}
                  value={currentKeywords}
                  options={currentKeywords.map((keyword) => ({
                    label: keyword,
                    value: keyword,
                  }))}
                  onChange={(v) => {
                    if (v.length === 0) {
                      setFieldValue(SCRAPER_REQ_FIELD_KEY.KEYWORDS, undefined);
                    } else {
                      setFieldValue(SCRAPER_REQ_FIELD_KEY.KEYWORDS, v);
                    }
                  }}
                  mode="tags"
                  onSelect={(value) => {
                    if (!currentKeywords.includes(value)) {
                      setFieldValue(SCRAPER_REQ_FIELD_KEY.KEYWORDS, [
                        ...currentKeywords,
                        value,
                      ]);
                    }
                  }}
                />
                {errors.length > 0 && (
                  <div className="text-destructive text-xs">{errors[0]}</div>
                )}
              </div>
              <div
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => modal.open()}
              >
                <Lightbulb className="text-gray-600 size-3.5" />
                <Typo type="label3" weight="m" className="text-gray-600">
                  Quick tips: Use & (and), | (or), ! (not), "quotes"
                </Typo>
              </div>
              <Modal
                status={modal.status}
                closeModal={modal.close}
                modalZIndex={zIndex.modal + 10}
                useBackgroundClose
              >
                <OperatorReferenceModal />
              </Modal>
            </div>
          );
        }}
      </Form.Item>
    </>
  );
};

const OperatorReferenceModal = () => {
  return (
    <ModalLayout className="w-100">
      <Modal.Header className="pb-4">
        <Modal.Title>Operator reference</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <RefItem
          operator="&"
          description="Search for results containing both keywords (e.g., coffee & beans)"
        />
        <Divider />
        <RefItem
          operator="|"
          description="Search for results containing either keyword  (e.g., latte | cappuccino)"
        />
        <Divider />
        <RefItem
          operator="!"
          description="Exclude specific words from results (e.g., decaf !caffeine)"
        />
        <Divider />
        <RefItem
          operator='"'
          description='Search for exact words or phrases (e.g., "iced americano")'
        />
      </Modal.Body>
      <Space h={16} />
    </ModalLayout>
  );
};

const RefItem = (props: { operator: string; description: string }) => {
  return (
    <div className="flex items-center gap-1">
      <Typo type="body2" weight="r" className="text-gray-700 p-3">
        {props.operator}
      </Typo>
      <Typo type="body2" weight="r" className="text-gray-700 p-3">
        {props.description}
      </Typo>
    </div>
  );
};
